import { Component, OnInit } from '@angular/core';
import { BillService } from 'src/app/services/core/quickbook/bills/bill.service';
import { VendorService } from 'src/app/services/core/quickbook/vendors/vendor.service';
import { NUMBER_OF_RECORDS, BILL_PAYMENT_STAT_CARDS, BILL_AMOUNT_STAT_CARDS, getAmountPaid, getPaymentStatus, getPaymentStatusClass, getRemainingBalance } from 'src/app/utils/common.utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, debounceTime, filter } from 'rxjs';
import * as moment from 'moment';
import { sweetAlert } from 'src/app/utils/swal';
import { Router, ActivatedRoute } from '@angular/router';

@UntilDestroy()

@Component({
  selector: 'app-bills-list',
  templateUrl: './bills-list.component.html',
  styleUrls: ['./bills-list.component.css']
})
export class BillsListComponent implements OnInit {

  bills: any = [];
  totalPages = 0;
  totalResults = 0;
  currentPage: number = 1;
  billDetail: any;
  tripId: any = null;
  referencePicker: any = null;
  selectedDropdownIndex: any;
  toggleActionButtons = false;
  search: any = '';
  private searchSubject = new Subject<string>();
  vendors: any;
  selectedVendorId = null;
  selectedPaymentStatus = '';
  billDateRange: any = [moment().startOf('day').toDate(), moment().endOf('day').toDate()];
  paymentStats: any = {};
  paymentStatCards = BILL_PAYMENT_STAT_CARDS;
  amountStatCards = BILL_AMOUNT_STAT_CARDS;

  numberOfRecords = NUMBER_OF_RECORDS;
  getPaymentStatus = getPaymentStatus;
  getPaymentStatusClass = getPaymentStatusClass;
  getAmountPaid = getAmountPaid;
  getRemainingBalance = getRemainingBalance;

  // one trip opens its detail directly, several ids ask which trip first
  openReference(reference: any) {
    const ids = (reference?.id || '').split(',').map((id: string) => id.trim()).filter(Boolean);
    if (ids.length > 1) this.referencePicker = reference;
    else this.tripId = ids[0];
  }

  constructor(
    private billService: BillService,
    private vendorService: VendorService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.selectedPaymentStatus = this.route.snapshot.queryParams['paymentStatus'] || '';
    this.initializeSearch();
    this.getAllVendors();
    this.getAllBills(this.currentPage);
  }

  getQueryParams(page?: number) {
    const queryParams: any = {};
    if (page != null) {
      queryParams.limit = String(this.numberOfRecords);
      queryParams.page = String(page);
    }
    if (this.search) {
      queryParams.search = this.search.trim();
    }
    if (this.selectedPaymentStatus) {
      queryParams.paymentStatus = this.selectedPaymentStatus;
    }
    if (this.selectedVendorId) {
      queryParams.vendor = this.selectedVendorId;
    }
    const dates = this.getBillDateParams();
    if (dates.startDate) {
      queryParams.startDate = dates.startDate;
      queryParams.endDate = dates.endDate;
    }
    return queryParams;
  }

  getAllBills(page: any) {
    this.billService.getBills(this.getQueryParams(page)).pipe(untilDestroyed(this)).subscribe({
      next: (data: any) => {
        if (data) {
          const { data: rows = [], overview = {} } = (data?.[0] || {}) as any;
          this.bills = rows;
          this.totalPages = overview.totalPages;
          this.totalResults = overview.totalResults;
          this.paymentStats = overview;
        }
      },
      error: (err) => {
        sweetAlert('Error', err, 'error', 'Ok');
      }
    })
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.getAllBills(page);
  }

  getAllVendors() {
    this.vendorService.getVendors({ limit: 9999 })
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.vendors = (data.results || []).filter((vendor: any) => vendor.Id)
        }
      })
  }

  refreshBills(resetPage = false) {
    if (resetPage) {
      this.currentPage = 1;
    }
    this.getAllBills(this.currentPage);
  }

  onVendorFilterChange() {
    this.refreshBills(true);
  }

  onBillDateChange(event: any) {
    if (!event?.[0] && !event?.[1]) {
      this.billDateRange = null;
      this.refreshBills(true);
      return;
    }
    if (!event?.[0] || !event?.[1]) {
      return;
    }
    this.billDateRange = event;
    this.refreshBills(true);
  }

  getBillDateParams() {
    if (!this.billDateRange?.[0] || !this.billDateRange?.[1]) {
      return {};
    }
    return {
      startDate: moment(this.billDateRange[0]).format('YYYY-MM-DD'),
      endDate: moment(this.billDateRange[1]).format('YYYY-MM-DD'),
    };
  }

  onPaymentStatusFilterChange() {
    this.refreshBills(true);
  }

  async onSyncBill(bill: any, type: any) {
    const mPull = `Are you sure you want to overwrite bill data with QuickBooks data?`
    const mPush = 'Are you sure you want to overwrite the QuickBooks data with bill data'
    const result = await sweetAlert('Alert', type === 'pull' ? mPull : mPush, 'warning', 'Yes', 'No');
    if (!result.value) return;

    const queryParams = {
      isSyncFrom: type === 'pull' ? true : false
    }
    if (type === 'pull') {
      bill.pulling = true;
    } else {
      bill.pushing = true;
    }

    this.billService.syncBill(bill.id, queryParams).subscribe(
      {
        next: (data: any) => {
          this.refreshBills(false);
          sweetAlert('Success', data?.message ||  'Synced successfully', 'success', 'Ok');
        },
        error: (err) => {
          console.error(err);
          sweetAlert('Error', err, 'error', 'Ok');
          bill.pulling = false;
          bill.pushing = false;
        },
        complete: () => {
          bill.pulling = false;
          bill.pushing = false;
        }
      })
  }

  showActionButtons(idx: any) {
    this.selectedDropdownIndex = idx;
    this.toggleActionButtons = true;
  }

  closeActionMenu(evt: any) {
    this.selectedDropdownIndex = -1;
    this.toggleActionButtons = false;
  }

  sendBill(bill: any) {
    this.billService.sendBill(bill.id).pipe(untilDestroyed(this)).subscribe({
      next: (data: any) => {
        this.toggleActionButtons = false;
      },
      error: (err) => {
        sweetAlert('Error', err, 'error', 'Ok');
      }
    });
  }

  async deleteBill(bill: any) {
    const result = await sweetAlert('Alert', 'Are you sure you want to delete this bill?', 'warning', 'Yes', 'No');
    if (!result.value) return;

    this.billService.deleteBill(bill.id).pipe(untilDestroyed(this)).subscribe(() => {
      this.refreshBills(false);
      this.toggleActionButtons = false;
    }, err => {
      sweetAlert('Error', err, 'error', 'Ok');
    });
  }

  openDetailModal(bill: any) {
    this.billDetail = bill;
  }

  initializeSearch() {
    this.searchSubject
      .pipe(
        debounceTime(500),
        filter((search: any) => !search || search.length >= 3),
        untilDestroyed(this)
      ).subscribe(() => {
        this.currentPage = 1;
        this.refreshBills(true);
      });
  }

  onSearchInput() {
    this.searchSubject.next(this.search);
  }

  openEditBill(billId: any) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/quickbook/bills/${billId}/edit`])
    );
    window.open(url, "_blank");
  }

  openPayBill(billId: any) {
    this.router.navigateByUrl(`/quickbook/bills/${billId}/pay`);
  }
}
